import {
  faChevronDown,
  faChevronUp,
  faEdit,
  faEllipsisVertical,
  faRotate,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useAppContext } from "../../../../../../../context/app.context";
import { OrderAddRouteWaypointGroupWaypoint } from "../../../types/order-add-route-waypoint";
import ButtonComponent from "../../../../../../../common/components/button/button.component";
import NumericInputComponent from "../../../../../../../common/components/form/input/numeric-input/numeric-input.component";
import orderTranslationsHelper from "../../../../../../../languages/order-translations.helper";
import debounce from "lodash/debounce";
import orderAddFactory from "../../../factory/order-add.factory";
import OrderAddCargoAddress from "../../../types/order-add-cargo-address";
import OrderAddCargoCompany from "../../../types/order-add-cargo-company";
import OrderAddPassengerListItem from "../../../types/order-add-passenger-list-item";
import OrderAddRouteAddress from "../../../types/order-add-route-address";
import OrderAddRouteAddressSelectOption from "../../../types/order-add-route-address-select-option";
import OrderAddRoutesPassengerIconComponent from "../../passenger-icon/order-add-routes-passenger-icon.component";
import orderAddRoutesFactory from "../order-add-routes.factory";
import orderAddRoutesHelper from "../order-add-routes.helper";
import orderAddRouteWaypointHelper from "./order-add-route-waypoint.helper";
import SingleSelectComponent from "../../../../../../../common/components/form/select/single-select/single-select.component";
import DateTimeInputComponent from "../../../../../../../common/components/form/input/date-time/date-time-input.component";
import SelectOption from "../../../../../../../common/components/form/select/common/option/select-option";
import useOpen from "../../../../../../../common/hooks/use-open";
import RoutesWaypointAddressAddFormComponent from "../../../../../common/routes/waypoint-address-add-form/routes-waypoint-address-add-form.component";
import RoutesWaypointAddressAddFormAddressData from "../../../../../common/routes/waypoint-address-add-form/types/routes-waypoint-address-add-form-address-data";
import useCargoCompanyAddressList from "../../../../../../../common/services/cargo-company/address/list/use-cargo-company-address-list";
import useAbort from "../../../../../../../common/hooks/use-abort";
import CargoCompanyAddressListParams from "../../../../../../../common/services/cargo-company/address/list/cargo-company-address-list-params";
import { DraggableProvidedDragHandleProps } from "@hello-pangea/dnd";

type OrderAddRouteGroupWaypointProps = {
  number: number;
  waypoint: OrderAddRouteWaypointGroupWaypoint;
  updateWaypoint: (updatedWaypoint: OrderAddRouteWaypointGroupWaypoint) => void;
  deleteWaypoint: () => void;
  onboardingPassengerCandidateList: OrderAddPassengerListItem[];
  outboardingPassengerCandidateList: OrderAddPassengerListItem[];
  onSeparateWaypointIntoGroupButtonClick: () => void;
  passengerList: OrderAddPassengerListItem[];
  selectedCargoCompanyUuid: OrderAddCargoCompany["uuid"] | null;
  dragHandleProps: DraggableProvidedDragHandleProps | null | undefined;
};

const OrderAddRouteGroupWaypointComponent: FC<
  OrderAddRouteGroupWaypointProps
> = (props) => {
  const translations = orderTranslationsHelper.getAddTranslations().routes;

  const cargoCompanyAddressList = useCargoCompanyAddressList();
  const cargoCompanyAddressListAbort = useAbort();

  const cargoAddressList: OrderAddCargoAddress[] = useMemo(() => {
    const addressList = orderAddFactory.createCargoAddresses(
      cargoCompanyAddressList.data,
    );

    return addressList;
  }, [cargoCompanyAddressList.data]);

  const [addressSearchQuery, setAddressSearchQuery] = useState("");

  const [
    isOnboardingPassengerListExpanded,
    setIsOnboardingPassengerListExpanded,
  ] = useState(false);

  const [
    isOutboardingPassengerListExpanded,
    setIsOutboardingPassengerListExpanded,
  ] = useState(false);

  const { selectedAppLanguage } = useAppContext();
  const { isOpen, open, close } = useOpen();

  const fetchAddressesDebounced = useCallback(
    debounce((selectedCargoCompanyUuid: string, addressSearchQuery: string) => {
      const params: CargoCompanyAddressListParams = {
        companyUuid: selectedCargoCompanyUuid,
        searchQuery: addressSearchQuery,
      };

      cargoCompanyAddressList.load(params, cargoCompanyAddressListAbort.signal);
    }, 500),
    [],
  );

  useEffect(() => {
    if (!props.selectedCargoCompanyUuid || !addressSearchQuery) {
      return;
    }

    fetchAddressesDebounced(props.selectedCargoCompanyUuid, addressSearchQuery);

    return () => {
      fetchAddressesDebounced.cancel();
      cargoCompanyAddressListAbort.revoke();
    };
  }, [props.selectedCargoCompanyUuid, addressSearchQuery]);

  const changeAddress = (
    address: RoutesWaypointAddressAddFormAddressData | undefined,
  ) => {
    const updatedWaypointAddress: OrderAddRouteAddress | null =
      orderAddRoutesFactory.createUpdatedWaypointAddress(address);

    const updatedWaypoint: OrderAddRouteWaypointGroupWaypoint = {
      ...props.waypoint,
      address: updatedWaypointAddress,
    };

    props.updateWaypoint(updatedWaypoint);
  };

  const changeHaltingTimeMinutes = (haltingTimeMinutes: number | null) => {
    const updatedWaypoint: OrderAddRouteWaypointGroupWaypoint = {
      ...props.waypoint,
      haltingTimeMinutes: haltingTimeMinutes ?? null,
    };

    props.updateWaypoint(updatedWaypoint);
  };

  const addPassengerToOnboarding = (
    passengerCandidate: OrderAddPassengerListItem,
  ) => {
    const newOnboardingPassengerListItems: OrderAddRouteWaypointGroupWaypoint["onboardingPassengerListItems"] =
      [...props.waypoint.onboardingPassengerListItems, passengerCandidate];

    const updatedWaypoint: OrderAddRouteWaypointGroupWaypoint = {
      ...props.waypoint,
      onboardingPassengerListItems: newOnboardingPassengerListItems,
    };

    props.updateWaypoint(updatedWaypoint);
  };

  const removePassengerFromOnboarding = (passengerListItemUuid: string) => {
    const newOnboardingPassengerListItems: OrderAddRouteWaypointGroupWaypoint["onboardingPassengerListItems"] =
      props.waypoint.onboardingPassengerListItems.filter(
        (passengerListItem) => passengerListItem.uuid !== passengerListItemUuid,
      );

    const updatedWaypoint: OrderAddRouteWaypointGroupWaypoint = {
      ...props.waypoint,
      onboardingPassengerListItems: newOnboardingPassengerListItems,
    };

    props.updateWaypoint(updatedWaypoint);
  };

  const addPassengerToOutboarding = (
    passengerCandidate: OrderAddPassengerListItem,
  ) => {
    const newOutboardingPassengerListItems: OrderAddRouteWaypointGroupWaypoint["outboardingPassengerListItems"] =
      [...props.waypoint.outboardingPassengerListItems, passengerCandidate];

    const updatedWaypoint: OrderAddRouteWaypointGroupWaypoint = {
      ...props.waypoint,
      outboardingPassengerListItems: newOutboardingPassengerListItems,
    };

    props.updateWaypoint(updatedWaypoint);
  };

  const removePassengerFromOutboarding = (passengerListItemUuid: string) => {
    const newOutboardingPassengerListItems: OrderAddRouteWaypointGroupWaypoint["outboardingPassengerListItems"] =
      props.waypoint.outboardingPassengerListItems.filter(
        (passengerListItem) => passengerListItem.uuid !== passengerListItemUuid,
      );

    const updatedWaypoint: OrderAddRouteWaypointGroupWaypoint = {
      ...props.waypoint,
      outboardingPassengerListItems: newOutboardingPassengerListItems,
    };

    props.updateWaypoint(updatedWaypoint);
  };

  const addressSelectOptions = useMemo<
    OrderAddRouteAddressSelectOption[]
  >(() => {
    if (!addressSearchQuery) {
      const additionalAddress: SelectOption[] = [
        {
          label: translations.waypointAddressAddLabel,
          value: null,
          onClick: () => {
            open();
          },
        },
      ];

      return additionalAddress;
    }

    const addressSelectOptionsFromPassengerList: OrderAddRouteAddressSelectOption[] =
      props.passengerList
        .map((passengerListItem) => {
          return passengerListItem.passenger.addresses.map((address) => {
            return orderAddRoutesFactory.createRouteAddressSelectOptionFromPassengerAddress(
              passengerListItem.passenger,
              address,
            );
          });
        })
        .flat();

    const filteredAddressSelectOptionsFromPassengerList =
      addressSelectOptionsFromPassengerList.filter((option) => {
        return (
          option.label
            .toLowerCase()
            .includes(addressSearchQuery.toLowerCase()) ||
          option.subText
            ?.toLowerCase()
            .includes(addressSearchQuery.toLowerCase())
        );
      });

    const addressSelectOptionsFromCargoAddresses: OrderAddRouteAddressSelectOption[] =
      cargoAddressList.map((cargoAddress) => {
        return orderAddRoutesFactory.createRouteAddressSelectOptionFromCargoAddress(
          cargoAddress,
        );
      });

    const selectOptions: OrderAddRouteAddressSelectOption[] = [
      ...filteredAddressSelectOptionsFromPassengerList,
      ...addressSelectOptionsFromCargoAddresses,
    ];

    return selectOptions;
  }, [
    props.passengerList,
    cargoAddressList,
    addressSearchQuery,
    cargoCompanyAddressList.isLoading,
    selectedAppLanguage,
  ]);

  const addressSelectOption = props.waypoint.address
    ? orderAddFactory.createRouteAddressSelectOption(props.waypoint.address)
    : null;

  const ExpandOnboardingPassengerListButton = (
    <ButtonComponent
      onClick={() => setIsOnboardingPassengerListExpanded(true)}
      title={translations.waypoint.onboardingPassengersExpandButtonTitle}
      classNames={{
        root: "order_add_route_list_item_onboarding__collapse_button",
      }}
      idForTesting={`route-list-item-waypoint-${props.waypoint.uuid}-expand-onboarding-passengers-button`}
    >
      <FontAwesomeIcon icon={faChevronDown} />
    </ButtonComponent>
  );

  const CollapseOnboardingPassengerListButton = (
    <ButtonComponent
      onClick={() => setIsOnboardingPassengerListExpanded(false)}
      title={translations.waypoint.onboardingPassengersCollapseButtonTitle}
      classNames={{
        root: "order_add_route_list_item_onboarding__collapse_button",
      }}
      idForTesting={`route-list-item-waypoint-${props.waypoint.uuid}-collapse-onboarding-passengers-button`}
    >
      <FontAwesomeIcon icon={faChevronUp} />
    </ButtonComponent>
  );

  const ExpandOutboardingPassengerListButton = (
    <ButtonComponent
      onClick={() => setIsOutboardingPassengerListExpanded(true)}
      title={translations.waypoint.outboardingPassengersExpandButtonTitle}
      classNames={{
        root: "order_add_route_list_item_onboarding__collapse_button",
      }}
      idForTesting={`route-list-item-waypoint-${props.waypoint.uuid}-expand-outboarding-passengers-button`}
    >
      <FontAwesomeIcon icon={faChevronDown} />
    </ButtonComponent>
  );

  const CollapseOutboardingPassengerListButton = (
    <ButtonComponent
      onClick={() => setIsOutboardingPassengerListExpanded(false)}
      title={translations.waypoint.outboardingPassengersCollapseButtonTitle}
      classNames={{
        root: "order_add_route_list_item_onboarding__collapse_button",
      }}
      idForTesting={`route-list-item-waypoint-${props.waypoint.uuid}-collapse-outboarding-passengers-button`}
    >
      <FontAwesomeIcon icon={faChevronUp} />
    </ButtonComponent>
  );

  return (
    <div className="order_add_route_list_item">
      <div className="order_add_route_list_item__top_row">
        <button
          className="order_add_route_list_item__drag_button"
          title={translations.waypoint.dragButtonTitle}
          {...props.dragHandleProps}
        >
          <FontAwesomeIcon icon={faEllipsisVertical} />
        </button>
        <div className="order_add_route_list_item__index">{props.number}</div>
        <ButtonComponent
          onClick={() => {
            open();
          }}
          title={translations.waypoint.waypointAddressEditLabel}
          classNames={{
            root: "order_add_route_list_item__action_button",
          }}
          isDisabled={!props.waypoint.address}
          idForTesting={`route-list-item-${props.waypoint.uuid}-address-edit-button`}
        >
          <FontAwesomeIcon icon={faEdit} />
        </ButtonComponent>
        <SingleSelectComponent
          onChange={(value) => changeAddress(value?.value)}
          options={addressSelectOptions}
          defaultValue={addressSelectOption}
          value={addressSelectOption}
          placeholder={translations.waypoint.addressSelectText}
          classNames={{
            root: `order_add_route_list_item__address`,
          }}
          isLoading={cargoCompanyAddressList.isLoading}
          isDisabled={cargoCompanyAddressList.isError}
          maxMenuHeight={500}
          isSearchable
          filterFunction={() => true}
          inputValue={addressSearchQuery}
          onInputChange={setAddressSearchQuery}
          isClearable
          noOptionsMessage={(inputValue) => {
            if (inputValue) {
              return translations.waypoint.addressSelectSearchNoOptionsMessage;
            }
            return translations.waypoint.addressSelectSearchTipMessage;
          }}
          idForTesting={`route-list-item-waypoint-${props.waypoint.uuid}-address-select`}
        />
        <div
          className="order_add_route_list_item__date"
          title={translations.waypoint.datePickerDisabledReasonTitle}
        >
          <DateTimeInputComponent
            date={null}
            onChange={() => {}}
            isDisabled
            idForTesting={`route-list-item-waypoint-${props.waypoint.uuid}-data-input`}
          />
        </div>
        <NumericInputComponent
          placeholder={translations.waypoint.haltingTimeInput}
          value={props.waypoint.haltingTimeMinutes}
          onChange={(value) => {
            changeHaltingTimeMinutes(value);
          }}
          classNames={{
            root: "order_add_route_list_item__halting_time",
          }}
          idForTesting={`route-list-item-waypoint-${props.waypoint.uuid}-halting-time-input`}
        />
        <ButtonComponent
          onClick={props.deleteWaypoint}
          classNames={{
            root: "order_add_route_list_item__action_button",
          }}
          idForTesting={`route-list-item-waypoint-${props.waypoint.uuid}-delete-button`}
        >
          <FontAwesomeIcon icon={faTrash} />
        </ButtonComponent>
      </div>
      <div className="order_add_route_list_item__bottom_row">
        <div className="order_add_route_list_item_onboarding">
          <div className="order_add_route_list_item_onboarding__collapsed">
            <ul className="order_add_route_list_item_onboarding__collapsed_passenger_list">
              {props.waypoint.onboardingPassengerListItems.map(
                (onboardingPassengerListItem) => {
                  const passengerIndex =
                    orderAddRoutesHelper.getIndexOfPassenger(
                      props.passengerList,
                      onboardingPassengerListItem.uuid,
                    );

                  if (passengerIndex === undefined) {
                    return null;
                  }

                  const iconTitle =
                    orderAddRouteWaypointHelper.getPassengerIconTitle(
                      onboardingPassengerListItem.passenger,
                    );

                  return (
                    <li
                      key={`onboarded-passenger-${onboardingPassengerListItem}`}
                      className="order_add_route_list_item_onboarding__collapsed_passenger_list_item"
                      title={iconTitle}
                    >
                      <OrderAddRoutesPassengerIconComponent
                        number={passengerIndex + 1}
                      />
                    </li>
                  );
                },
              )}
            </ul>
            {isOnboardingPassengerListExpanded
              ? CollapseOnboardingPassengerListButton
              : ExpandOnboardingPassengerListButton}
          </div>
          {isOnboardingPassengerListExpanded && (
            <>
              <div>
                <ul className="order_add_route_list_item_onboarding__expanded_passenger_list">
                  {props.waypoint.onboardingPassengerListItems.map(
                    (onboardingPassengerListItem) => {
                      const { passenger } = onboardingPassengerListItem;

                      const passengerIndex =
                        orderAddRoutesHelper.getIndexOfPassenger(
                          props.passengerList,
                          onboardingPassengerListItem.uuid,
                        );

                      if (passengerIndex === undefined) {
                        return null;
                      }

                      const buttonLabel =
                        orderAddRouteWaypointHelper.getPassengerButtonLabel(
                          passenger,
                        );

                      const buttonTitle =
                        orderAddRouteWaypointHelper.getPassengerButtonTitle(
                          passenger,
                        );

                      return (
                        <li
                          key={`onboarded-passenger-candidate-${onboardingPassengerListItem.uuid}`}
                          className="order_add_route_list_item_onboarding__expanded_passenger_list_item"
                          title={buttonTitle}
                        >
                          <OrderAddRoutesPassengerIconComponent
                            number={passengerIndex + 1}
                          />
                          <ButtonComponent
                            onClick={() =>
                              removePassengerFromOnboarding(
                                onboardingPassengerListItem.uuid,
                              )
                            }
                            classNames={{
                              root: "order_add_route_list_item_onboarding__passenger_button",
                            }}
                            idForTesting={`onboarded-passenger-candidate-${onboardingPassengerListItem.uuid}-remove-button`}
                          >
                            {buttonLabel}
                          </ButtonComponent>
                        </li>
                      );
                    },
                  )}
                </ul>
                <ul className="order_add_route_list_item_onboarding__passenger_candidate_list">
                  {props.onboardingPassengerCandidateList.map(
                    (onboardingPassengerCandidateListItem) => {
                      const { passenger } =
                        onboardingPassengerCandidateListItem;

                      const passengerIndex =
                        orderAddRoutesHelper.getIndexOfPassenger(
                          props.passengerList,
                          onboardingPassengerCandidateListItem.uuid,
                        );

                      if (passengerIndex === undefined) {
                        return null;
                      }

                      const buttonLabel =
                        orderAddRouteWaypointHelper.getPassengerButtonLabel(
                          passenger,
                        );

                      const buttonTitle =
                        orderAddRouteWaypointHelper.getPassengerButtonTitle(
                          passenger,
                        );

                      return (
                        <li
                          className="order_add_route_list_item_onboarding__expanded_passenger_list_item"
                          title={buttonTitle}
                        >
                          <OrderAddRoutesPassengerIconComponent
                            number={passengerIndex + 1}
                          />
                          <ButtonComponent
                            onClick={() =>
                              addPassengerToOnboarding(
                                onboardingPassengerCandidateListItem,
                              )
                            }
                            classNames={{
                              root: "order_add_route_list_item_onboarding__passenger_candidate_button",
                            }}
                            idForTesting={`onboarded-passenger-candidate-${onboardingPassengerCandidateListItem.uuid}-add-button`}
                          >
                            {buttonLabel}
                          </ButtonComponent>
                        </li>
                      );
                    },
                  )}
                </ul>
              </div>
            </>
          )}
        </div>
        <div>
          <ButtonComponent
            onClick={props.onSeparateWaypointIntoGroupButtonClick}
            title={translations.waypoint.separateWaypointButtonTitle}
            idForTesting={`route-list-item-${props.waypoint.uuid}-separate-group-button`}
          >
            <FontAwesomeIcon icon={faRotate} size="sm" />
          </ButtonComponent>
        </div>
        <div className="order_add_route_list_item_outboarding">
          <div className="order_add_route_list_item_outboarding__collapsed">
            <ul className="order_add_route_list_item_outboarding__collapsed_passenger_list">
              {props.waypoint.outboardingPassengerListItems.map(
                (outboardingPassengerListItem) => {
                  const passengerIndex =
                    orderAddRoutesHelper.getIndexOfPassenger(
                      props.passengerList,
                      outboardingPassengerListItem.uuid,
                    );

                  if (passengerIndex === undefined) {
                    return null;
                  }

                  const iconTitle =
                    orderAddRouteWaypointHelper.getPassengerIconTitle(
                      outboardingPassengerListItem.passenger,
                    );

                  return (
                    <li
                      className="order_add_route_list_item_outboarding__collapsed_passenger_list_item"
                      title={iconTitle}
                    >
                      <OrderAddRoutesPassengerIconComponent
                        number={passengerIndex + 1}
                      />
                    </li>
                  );
                },
              )}
            </ul>
            {isOutboardingPassengerListExpanded
              ? CollapseOutboardingPassengerListButton
              : ExpandOutboardingPassengerListButton}
          </div>
          {isOutboardingPassengerListExpanded && (
            <>
              <ul className="order_add_route_list_item_outboarding__expanded_passenger_list">
                {props.waypoint.outboardingPassengerListItems.map(
                  (outboardingPassengerListItem) => {
                    const { passenger } = outboardingPassengerListItem;

                    const passengerIndex =
                      orderAddRoutesHelper.getIndexOfPassenger(
                        props.passengerList,
                        outboardingPassengerListItem.uuid,
                      );

                    if (passengerIndex === undefined) {
                      return null;
                    }

                    const buttonLabel =
                      orderAddRouteWaypointHelper.getPassengerButtonLabel(
                        passenger,
                      );

                    const buttonTitle =
                      orderAddRouteWaypointHelper.getPassengerButtonTitle(
                        passenger,
                      );

                    return (
                      <li
                        key={`outboarding-passenger-candidate-${outboardingPassengerListItem}`}
                        className="order_add_route_list_item_outboarding__expanded_passenger_list_item"
                        title={buttonTitle}
                      >
                        <OrderAddRoutesPassengerIconComponent
                          number={passengerIndex + 1}
                        />
                        <ButtonComponent
                          onClick={() =>
                            removePassengerFromOutboarding(
                              outboardingPassengerListItem.uuid,
                            )
                          }
                          classNames={{
                            root: "order_add_route_list_item_outboarding__passenger_button",
                          }}
                          idForTesting={`outboarding-passenger-candidate-${outboardingPassengerListItem}-remove-button`}
                        >
                          {buttonLabel}
                        </ButtonComponent>
                      </li>
                    );
                  },
                )}
              </ul>
              <ul className="order_add_route_list_item_outboarding__passenger_candidate_list">
                {props.outboardingPassengerCandidateList.map(
                  (outboardingPassengerCandidateListItem) => {
                    const { passenger } = outboardingPassengerCandidateListItem;

                    const passengerIndex =
                      orderAddRoutesHelper.getIndexOfPassenger(
                        props.passengerList,
                        outboardingPassengerCandidateListItem.uuid,
                      );

                    if (passengerIndex === undefined) {
                      return null;
                    }

                    const buttonLabel =
                      orderAddRouteWaypointHelper.getPassengerButtonLabel(
                        passenger,
                      );

                    const buttonTitle =
                      orderAddRouteWaypointHelper.getPassengerButtonTitle(
                        passenger,
                      );

                    return (
                      <li
                        className="order_add_route_list_item_outboarding__expanded_passenger_list_item"
                        title={buttonTitle}
                      >
                        <OrderAddRoutesPassengerIconComponent
                          number={passengerIndex + 1}
                        />
                        <ButtonComponent
                          onClick={() =>
                            addPassengerToOutboarding(
                              outboardingPassengerCandidateListItem,
                            )
                          }
                          classNames={{
                            root: "order_add_route_list_item_outboarding__passenger_candidate_button",
                          }}
                          idForTesting={`outboarding-passenger-candidate-${outboardingPassengerCandidateListItem}-add-button`}
                        >
                          {buttonLabel}
                        </ButtonComponent>
                      </li>
                    );
                  },
                )}
              </ul>
            </>
          )}
        </div>
      </div>
      <RoutesWaypointAddressAddFormComponent
        isOpen={isOpen}
        onClose={close}
        onAddNewAddress={changeAddress}
      />
    </div>
  );
};

export default OrderAddRouteGroupWaypointComponent;
